var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import api from '@utils/axios';
import { mapComponentsResponseToFrontend, mapDragDropResultToReorderRequest, mapCreateComponentFormToRequest, mapUpdateDataToRequest } from '../mapper';
// Base URL builder for components endpoints
function buildComponentsUrl(filterParams, componentId, action) {
    const baseUrl = '/api/internal/components';
    const params = new URLSearchParams();
    // Add buildingId or attachableId to query params
    if (filterParams.buildingId) {
        params.append('buildingId', String(filterParams.buildingId));
    }
    if (filterParams.attachableId) {
        params.append('attachableId', String(filterParams.attachableId));
    }
    if (filterParams.attachableType) {
        params.append('attachableType', filterParams.attachableType);
    }
    const queryString = params.toString();
    // Build the path with optional component ID and action
    let path = baseUrl;
    if (componentId) {
        path += `/${componentId}`;
    }
    if (action) {
        path += `/${action}`;
    }
    // Append query string if present
    return queryString ? `${path}?${queryString}` : path;
}
// Get all components filtered by building or attachable
export function getComponents(filterParams) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        try {
            const response = yield api.get(buildComponentsUrl(filterParams));
            if (!response.data) {
                throw new Error('No data received from server');
            }
            const transformedData = mapComponentsResponseToFrontend(response.data);
            return {
                data: transformedData
            };
        }
        catch (error) {
            console.error('Error fetching components:', error);
            return {
                error: {
                    message: ((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) || error.message || 'Failed to fetch components',
                    errors: (_d = (_c = error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors
                }
            };
        }
    });
}
// Toggle active state of a component (activate/deactivate with cascade)
export function toggleActiveComponent(filterParams, componentId) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        try {
            yield api.patch(buildComponentsUrl(filterParams, componentId, 'toggle_active'));
            return {};
        }
        catch (error) {
            console.error('Error toggling component active state:', error);
            const errorMessage = ((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) || error.message || 'Failed to toggle component active state';
            return {
                error: {
                    message: errorMessage,
                    errors: (_d = (_c = error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors
                }
            };
        }
    });
}
// Reorder components after drag and drop
export function reorderComponents(filterParams, dragDropResult) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        try {
            const reorderRequest = mapDragDropResultToReorderRequest(dragDropResult);
            yield api.patch(buildComponentsUrl(filterParams, undefined, 'reorder'), reorderRequest);
            return {};
        }
        catch (error) {
            console.error('Error reordering components:', error);
            return {
                error: {
                    message: ((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) || error.message || 'Failed to reorder components',
                    errors: (_d = (_c = error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors
                }
            };
        }
    });
}
// Create a new component
export function createComponent(filterParams, formData) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        try {
            // Map form data to API request format using mapper
            const requestData = mapCreateComponentFormToRequest(formData);
            const response = yield api.post(buildComponentsUrl(filterParams), {
                component: requestData
            });
            if (!response.data) {
                throw new Error('No data received from server');
            }
            return {
                data: response.data
            };
        }
        catch (error) {
            console.error('Error creating component:', error);
            return {
                error: {
                    message: ((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) || error.message || 'Failed to create component',
                    errors: (_d = (_c = error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors
                }
            };
        }
    });
}
// Update an existing component
export function updateComponent(filterParams, componentId, updateData) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        try {
            // Convert the field/value pairs to proper API format using mapper
            const requestData = mapUpdateDataToRequest(updateData);
            const response = yield api.patch(buildComponentsUrl(filterParams, componentId), {
                component: requestData
            });
            if (!response.data) {
                throw new Error('No data received from server');
            }
            return {
                data: response.data
            };
        }
        catch (error) {
            console.error('Error updating component:', error);
            return {
                error: {
                    message: ((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) || error.message || 'Failed to update component',
                    errors: (_d = (_c = error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors
                }
            };
        }
    });
}
// Refresh components data (useful after mutations)
export function refreshComponents(filterParams) {
    return __awaiter(this, void 0, void 0, function* () {
        return getComponents(filterParams);
    });
}
// Export individual functions and default object
export default {
    getComponents,
    createComponent,
    updateComponent,
    toggleActiveComponent,
    reorderComponents,
    refreshComponents
};
